import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type NavigationGuardNext,
  type RouteLocationNormalized,
} from 'vue-router'

export const ROUTE_NAMES = {
  HOME: 'home',
  PROFILE: 'profile',
  ADMIN: 'admin',
  ADMIN_USERS: 'admin-users',
  NEW_USER: 'newuser',
  USER: 'user',
  CALLBACK: 'callback',
  NOT_FOUND: '404',
  ERROR: 'error',
} as const

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: { name: ROUTE_NAMES.HOME },
  },
  {
    path: '/home',
    name: ROUTE_NAMES.HOME,
    component: () => import('@/views/Home.vue'),
  },
  {
    path: '/profile',
    name: ROUTE_NAMES.PROFILE,
    component: () => import('@/views/Profile.vue'),
  },
  {
    path: '/admin',
    name: ROUTE_NAMES.ADMIN,
    component: () => import('@/views/Admin.vue'),
    meta: { manager: true },
    redirect: { name: ROUTE_NAMES.ADMIN_USERS },
    children: [
      {
        path: 'users',
        name: ROUTE_NAMES.ADMIN_USERS,
        component: () => import('@/views/Users.vue'),
      },
      {
        path: 'newuser',
        name: ROUTE_NAMES.NEW_USER,
        component: () => import('@/views/NewUser.vue'),
      },
      {
        path: 'users/:id',
        name: ROUTE_NAMES.USER,
        component: () => import('@/views/EditUser.vue'),
        props: true,
      },
    ],
  },
  {
    path: '/callback',
    name: ROUTE_NAMES.CALLBACK,
    component: () => import('@/views/Callback.vue'),
    meta: { anonymous: true },
  },
  {
    path: '/404',
    name: ROUTE_NAMES.NOT_FOUND,
    component: () => import('@/views/404.vue'),
  },
  {
    path: '/error',
    name: ROUTE_NAMES.ERROR,
    component: () => import('@/views/Error.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

let isInit = false

router.beforeEach(
  async (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    if (to.matched.some((record) => record.meta.anonymous)) {
      next()
      return
    }

    localStorage.setItem('page.current', `${globalThis.origin}${to.fullPath}`)

    if (!isInit) {
      const { useAuthStore } = await import('@/stores/auth')
      const { useAppStore } = await import('@/stores/app')
      const { useRegionStore } = await import('@/stores/region')

      const authStore = useAuthStore()
      const appStore = useAppStore()
      const regionStore = useRegionStore()

      await Promise.all([authStore.fetchSignedIn(), authStore.fetchUserDetail()])
      await regionStore.loadRegions()

      if (authStore.user.admin || authStore.user.manager) {
        await appStore.loadApplications()
      }

      isInit = true
    }

    if (to.matched.some((record) => record.meta.manager)) {
      const { useAuthStore } = await import('@/stores/auth')
      const authStore = useAuthStore()

      if (authStore.hasAnyRoleAdmin) {
        next()
      } else {
        next({ name: ROUTE_NAMES.ERROR, query: { type: 403 } })
      }
    } else {
      next()
    }
  },
)

export default router
